import React, { useState, useMemo } from 'react';
import { StyleSheet, View, Text, ActivityIndicator, Platform } from 'react-native';
import GameDropDownButtonOblong from './Button/GameDropDownButtonOblong';
import { GAME_TYPES_TYPE } from '../../constants/constants';
import GameDropDownScroll from './GameDropDownScroll';
import { useSignedInContext } from '../../context/SignedInContext';
import { useGymAssessmentPracticeGamesQuery } from '../screens/Gym/Stats/useAssessmentPracticeGamesQuery.gql';
import { OperationChart } from './OperationChart';

type RowData = {
  label: string;
  gamesPlayed: number;
  highScore: number;
  overallAccuracy: number;
  timePerQuestion: number;
};

type StatsTableAssessmentPracticeProps = {
  queryUserId?: string;
};

export default function StatsTableAssessmentPractice(props: StatsTableAssessmentPracticeProps) {
  const { user } = useSignedInContext();
  const userId = props.queryUserId || user?.id || '';

  const [gameSelected, setGameSelected] = useState<GAME_TYPES_TYPE | 'All'>(
    GAME_TYPES_TYPE.MULTI_1_12,
  );
  const [showDropdown, setShowDropdown] = useState(false);

  const {
    data: assessmentData,
    loading,
    error,
  } = useGymAssessmentPracticeGamesQuery(
    {
      userId,
      gameType: gameSelected === 'All' ? undefined : gameSelected,
    },
    { skip: !userId },
  );

  const correctAnswers = useMemo(() => {
    if (!assessmentData?.assessmentPracticeGamesByUserHiveAndGameType) return [];
    return assessmentData.assessmentPracticeGamesByUserHiveAndGameType.flatMap(
      g => g.correctAnswers,
    );
  }, [assessmentData]);

  const wrongAnswers = useMemo(() => {
    if (!assessmentData?.assessmentPracticeGamesByUserHiveAndGameType) return [];
    return assessmentData.assessmentPracticeGamesByUserHiveAndGameType.flatMap(g => g.wrongAnswers);
  }, [assessmentData]);

  // Aggregate data
  const rows: RowData[] = useMemo(() => {
    if (!assessmentData?.assessmentPracticeGamesByUserHiveAndGameType) return [];

    const modes = {
      FixedQuestions: '25 Questions',
      Timed: '60 Sec Challenge',
    } as const;

    return Object.entries(modes).map(([mode, label]) => {
      const games = assessmentData.assessmentPracticeGamesByUserHiveAndGameType.filter(
        g => g.assessmentMode === mode,
      );

      if (games.length === 0) {
        return {
          label,
          gamesPlayed: 0,
          highScore: 0,
          overallAccuracy: 0,
          timePerQuestion: 0,
        };
      }

      const gamesPlayed = games.reduce((sum, g) => sum + g.played, 0);
      const highScore = Math.max(...games.map(g => g.highScore));
      const totalCorrect = games.reduce((sum, g) => sum + g.correctCount, 0);
      const totalQuestions = games.reduce((sum, g) => sum + g.questionsTaken, 0);
      const totalTime = games.reduce((sum, g) => sum + g.timeSec, 0);

      const overallAccuracy =
        totalQuestions > 0 ? Math.round((totalCorrect / totalQuestions) * 100) : 0;
      const timePerQuestion = totalQuestions > 0 ? +(totalTime / totalQuestions).toFixed(1) : 0;

      return {
        label,
        gamesPlayed,
        highScore,
        overallAccuracy,
        timePerQuestion,
      };
    });
  }, [assessmentData]);

  return (
    <>
      <View style={styles.tableShadowContainer}>
        <View style={styles.tableContainer}>
          {/* Header */}
          <View style={styles.header}>
            <View style={styles.headerViewButton}>
              <GameDropDownButtonOblong
                gameSelected={gameSelected}
                setGameSelected={setGameSelected}
                setShowDropdown={() => setShowDropdown(!showDropdown)}
                color={'#FCEABB'}
              />
            </View>
            {['Games Played', 'High Score', 'Overall Accuracy', 'Time/ question'].map(column => (
              <View style={styles.headerViewStats} key={column}>
                <Text style={styles.headerText}>{column}</Text>
              </View>
            ))}
          </View>

          {/* Loading & Error */}
          {loading && <ActivityIndicator size="small" color="#333" />}
          {error && <Text style={{ color: 'red' }}>Error loading data</Text>}

          {/* Body */}
          {rows.map((row, idx) => (
            <View key={row.label}>
              <View style={styles.row}>
                <View style={styles.cellLabel}>
                  <Text style={styles.bodyText}>{row.label}</Text>
                </View>
                <View style={styles.cellStat}>
                  <Text style={styles.bodyText}>{row.gamesPlayed}</Text>
                </View>
                <View style={styles.cellStat}>
                  <Text style={styles.bodyText}>{row.highScore}</Text>
                </View>
                <View style={styles.cellStat}>
                  <Text style={styles.bodyText}>{row.overallAccuracy}%</Text>
                </View>
                <View style={styles.cellStat}>
                  <Text style={styles.bodyText}>{row.timePerQuestion}</Text>
                </View>
              </View>
              {/* Divider */}
              {idx < rows.length - 1 && <View style={styles.divider} />}
            </View>
          ))}
        </View>
      </View>
      <View style={[styles.operationChartContainer, gameSelected === 'All' && { borderWidth: 0 }]}>
        <OperationChart
          gameType={gameSelected}
          wrongAnswers={wrongAnswers}
          correctAnswers={correctAnswers}
          width={Platform.OS === 'android' ? 360 : undefined}
        />
      </View>

      {showDropdown && (
        <GameDropDownScroll
          setShowDropdown={setShowDropdown}
          setGameSelected={setGameSelected}
          dropDownItemColor={'#FCEABB'}
        />
      )}
    </>
  );
}

const styles = StyleSheet.create({
  tableShadowContainer: {
    borderBottomWidth: 10,
    borderColor: '#FFCF53',
    borderRadius: 16,
  },
  tableContainer: {
    width: '100%',
    borderRadius: 16,
    backgroundColor: '#FEDD88',
    paddingHorizontal: 2,
    paddingVertical: 10,
    borderWidth: 2,
    borderColor: '#ffffff',
    position: 'relative',
  },
  header: {
    flexDirection: 'row',
    alignItems: 'center',
    borderBottomWidth: 1.5,
    borderColor: '#E5C87F',
    paddingBottom: 6,
    marginBottom: 4,
  },
  headerText: {
    fontFamily: 'Secular One',
    fontWeight: '700',
    fontSize: 12,
    color: '#333333',
  },
  headerViewButton: {
    flex: 1,
    alignItems: 'flex-start',
    justifyContent: 'center',
  },
  headerViewStats: {
    width: 53,
    alignItems: 'center',
    justifyContent: 'center',
  },
  row: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingVertical: 8,
  },
  cellLabel: {
    flex: 1,
    justifyContent: 'center',
    marginLeft: 8,
  },
  cellStat: {
    width: 55,
    alignItems: 'center',
    justifyContent: 'center',
  },
  bodyText: {
    fontFamily: 'Secular One',
    fontSize: 14,
    color: '#453B3B',
  },
  divider: {
    height: 1,
    backgroundColor: '#E5C87F',
    marginVertical: 2,
  },
  operationChartContainer: {
    marginTop: 20,
    alignItems: 'center',
    borderWidth: 1,
    borderBottomWidth: 5,
    borderColor: '#FFD322',
    borderRadius: 16,
  },
});
